import L from 'leaflet';

/**
 * Medición sobre el mapa: distancias y áreas.
 *
 * Todo es PLANO — sobre la proyección del terreno, como se ve desde arriba.
 * La superficie real de un terreno con lomas es mayor que su proyección, y
 * a veces por mucho: en el predio de Santa María son 153 ha planas contra
 * 185 de superficie. Para calcular semilla o riego, la buena es la segunda.
 *
 * Pero esa necesita el modelo de elevación, que es una capa aparte del
 * GeoTIFF de la imagen. Mientras no se suba, medir plano y DECIR que es
 * plano es lo honesto; presentar un número plano como si fuera la
 * superficie real sería equivocarse en más del 20% sin avisar.
 */

/** Radio de la Tierra según WGS84, en metros. */
const RADIO_TIERRA = 6378137;

const aRadianes = (g: number) => (g * Math.PI) / 180;

/** Suma de los tramos de una línea, en metros. */
export function distanciaTotal(puntos: L.LatLng[]): number {
  let total = 0;
  for (let i = 1; i < puntos.length; i++) {
    total += puntos[i - 1]!.distanceTo(puntos[i]!);
  }
  return total;
}

/**
 * Área de un polígono sobre la esfera, en metros cuadrados.
 *
 * Usa el exceso esférico y no una fórmula plana: sobre cientos de hectáreas
 * la curvatura ya se nota, y un predio no es un rectángulo en una hoja.
 * El polígono se cierra solo — el último punto se une con el primero.
 */
export function areaGeodesica(puntos: L.LatLng[]): number {
  if (puntos.length < 3) return 0;
  let suma = 0;
  for (let i = 0; i < puntos.length; i++) {
    const p1 = puntos[i]!;
    const p2 = puntos[(i + 1) % puntos.length]!;
    suma += aRadianes(p2.lng - p1.lng) * (2 + Math.sin(aRadianes(p1.lat)) + Math.sin(aRadianes(p2.lat)));
  }
  return Math.abs((suma * RADIO_TIERRA * RADIO_TIERRA) / 2);
}

/** Perímetro de un polígono cerrado, en metros. */
export function perimetro(puntos: L.LatLng[]): number {
  if (puntos.length < 3) return distanciaTotal(puntos);
  return distanciaTotal([...puntos, puntos[0]!]);
}

/**
 * Metros o kilómetros, según convenga leerlos.
 *
 * El corte está en 1 km: abajo de eso los metros son más útiles para
 * quien camina el terreno, y arriba los kilómetros evitan leer cinco
 * dígitos de corrido.
 */
export function formatearDistancia(metros: number, idioma: string): string {
  if (metros >= 1000) {
    return `${(metros / 1000).toLocaleString(idioma, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} km`;
  }
  return `${metros.toLocaleString(idioma, { maximumFractionDigits: 1 })} m`;
}

/**
 * Hectáreas o metros cuadrados.
 *
 * La hectárea es la unidad con la que se habla de un predio; abajo de una,
 * los metros cuadrados dicen más — una hectárea son 10,000 m².
 */
export function formatearArea(m2: number, idioma: string): string {
  if (m2 >= 10000) {
    return `${(m2 / 10000).toLocaleString(idioma, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ha`;
  }
  return `${m2.toLocaleString(idioma, { maximumFractionDigits: 0 })} m²`;
}
